import React, { useState } from 'react';
import axios from 'axios';
import { Form } from 'react-bootstrap';
import AdminSidebar from "../components/AdminSidebar";
import AdminNavbar from '../components/AdminNavbar';
import { useSidebar } from '../components/SidebarContext';
import QuestionModal from './QuestionModal';

const BulkUploadQuestions = () => {
  const [formData, setFormData] = useState({ section: '' });
  const [customSection, setCustomSection] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('No file chosen');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [errors, setErrors] = useState({});

  const { isSidebarOpen } = useSidebar();
  const sidebarWidth = isSidebarOpen ? '15.6rem' : '4.4rem';

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors(prev => ({ ...prev, section: '', customSection: '' }));
    if (name === 'section' && value !== 'other') {
      setCustomSection('');
    }
  };

  const handleCustomSectionChange = (e) => {
    setCustomSection(e.target.value);
    setErrors(prev => ({ ...prev, customSection: '' }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setFileName(file.name);
      setErrors(prev => ({ ...prev, file: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.section) {
      newErrors.section = 'Section is required';
    } else if (formData.section === 'other' && !customSection.trim()) {
      newErrors.customSection = 'Custom section is required';
    }
    if (!selectedFile) {
      newErrors.file = 'CSV file is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const formDataObj = new FormData();
    formDataObj.append('csvFile', selectedFile);
    formDataObj.append('section', formData.section === 'other' ? customSection.trim() : formData.section);

    setIsUploading(true);
    try {
      const response = await axios.post('/api/questions/bulk-upload', formDataObj, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadResults(response.data);
      alert(`Upload successful! ${response.data.successCount} questions added.`);
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Upload failed';
      alert(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const handleSaveQuestion = async (questionData) => {
    try {
      await axios.post('/api/questions', questionData);
      alert('Question added successfully!');
      setShowQuestionModal(false);
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      const validationErrors = error.response?.data?.validationErrors || [];
      alert(`Failed to save question: ${errorMessage}${validationErrors.length ? '\nDetails: ' + validationErrors.join(', ') : ''}`);
    }
  };

  // Updated Styles
    // Add a simple isMobile check
    const isMobile = window.innerWidth <= 768;
  
    const layoutStyles = {
      sidebar: { 
        position: 'fixed', 
        top: '0', 
        left: '0', 
        height: '100%', 
        width: sidebarWidth, 
        zIndex: '10', 
        transition: 'width 0.3s ease', 
        background: 'transparent' 
      },
      content: {
        padding: '2rem 3rem',
        backgroundColor: 'white',
        width: '100%',
        maxWidth: isSidebarOpen ? `calc(100vw - ${sidebarWidth} - 10rem)` : `calc(100vw - ${sidebarWidth} - 12rem)`, // Adjusted for better centering
        minHeight: '450px',
        maxHeight: 'calc(100vh - 8rem - 70px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        borderRadius: '0.75rem',
        boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
        transition: 'all 0.3s ease, max-width 0.3s ease',
        position: 'relative',
        zIndex: '5',
        marginRight: '2rem', // Shift the card slightly to the left
        ':hover': {
          boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
        },
      }
    };
  
    const styles = {
    titleContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1rem',
      marginLeft: '1rem', // Fixed typo: marginleft to marginLeft
      width: '100%',
    },
    title: {
      fontSize: '1rem',
      fontWeight: '600',
      color: '#1F2937',
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.5rem 1.2rem',
      fontSize: '0.875rem',
      fontWeight: '600',
      color: '#6B21A8',
      textTransform: 'uppercase',
      letterSpacing: '0.05rem',
      cursor: 'pointer',
      borderRadius: '1.5rem',
      border: '2px solid transparent',
      backgroundImage: 'linear-gradient(#ffffff, #ffffff), linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF)',
      backgroundOrigin: 'border-box',
      backgroundClip: 'padding-box, border-box',
      transition: 'all 0.4s ease',
    },
    form: { 
      display: 'flex', 
      flexDirection: 'column', 
      gap: '16px',
      width: '100%',
      flex: 1,
      justifyContent: 'flex-start',
    },
    fileInputContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      marginBottom: '0.5rem',
      width: '100%',
    },
    fileInput: { 
      display: 'none',
    },
    fileName: { 
      border: '1px solid #D1D5DB',
      borderRadius: '0.375rem',
      padding: '1rem',
      fontSize: '0.875rem',
      color: '#374151',
      backgroundColor: '#F9FAFB',
      width: '100%',
      maxWidth: '300px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      textAlign: 'center',
      marginTop: '0.3rem',
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    resultsContainer: {
      padding: '0.5rem',
      borderRadius: '0.375rem',
      marginBottom: '1rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      backgroundColor: uploadResults?.failedQuestions?.length > 0 ? '#FEE2E2' : '#DCFCE7',
      color: uploadResults?.failedQuestions?.length > 0 ? '#B91C1C' : '#166534',
      width: '100%',
      fontSize: '0.875rem',
    },
    successText: { 
      color: '#166534',
    },
    errorText: { 
      color: '#B91C1C',
    },
    errorList: {
      listStyleType: 'disc',
      paddingLeft: '20px',
      marginTop: '0.5rem',
      width: '100%',
    },
    errorItem: {
      fontSize: '0.875rem',
      color: '#B91C1C',
    },
    label: {
      fontSize: '0.875rem',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '0.3rem',
      display: 'block',
    },
    note: {
      fontSize: '0.875rem',
      color: '#6B7280',
      marginBottom: '0.5rem',
      textAlign: 'center',
      width: '100%',
    },
    uploadButtonContainer: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: '-0.5rem',
    },
  };

  return (
    <div style={layoutStyles.container}>
      <style>
        {`
        html, body {
          margin: 0;
          padding: 0;
          height: '100vh',
          width: '100vw',
          overflow: 'hidden',
        }
        @keyframes glow {
          0% { box-shadow: 0 0 12px rgba(100, 150, 255, 0.9), 0 0 18px rgba(180, 100, 255, 0.7); }
          50% { box-shadow: 0 0 18px rgba(100, 150, 255, 1), 0 0 25px rgba(180, 100, 255, 0.9); }
          100% { box-shadow: 0 0 12px rgba(100, 150, 255, 0.9), 0 0 18px rgba(180, 100, 255, 0.7); }
        }
        .glow-button:hover:not(:disabled) {
          background: linear-gradient(to right, #FFBBBB, #A8C1FF, #C5A8FF);
          transform: scale(1.05);
          animation: glow 1.5s ease-in-out infinite;
        }
        .input-field:focus {
          border-color: #6B21A8;
          outline: none;
          box-shadow: 0 0 5px rgba(107, 33, 168, 0.5);
        }
        `}
      </style>

      <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap" rel="stylesheet" />
      <div style={{ position: 'fixed', top: '0', left: '0', width: '100%', zIndex: '20', background: 'transparent' }}>
        <AdminNavbar />
      </div>
      <div style={layoutStyles.sidebar}>
        <AdminSidebar />
      </div>
      <div style={layoutStyles.content}>
        <div style={styles.titleContainer}>
          <h2 style={styles.title}>Bulk Upload Questions</h2>
          <button 
            style={styles.button} 
            onClick={() => setShowQuestionModal(true)} 
            className="glow-button"
          >
            Add a Single Question
          </button>
        </div>

        {uploadResults && (
          <div style={styles.resultsContainer}>
            <span>
              {uploadResults.successCount} questions uploaded successfully.
            </span>
            {uploadResults.failedQuestions?.length > 0 && (
              <ul style={styles.errorList}>
                {uploadResults.failedQuestions.map((item, index) => (
                  <li key={index} style={styles.errorItem}>
                    Line {item.line}: {item.reason}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div>
            <label style={styles.label}>Section</label>
            <Form.Select
              name="section"
              value={formData.section}
              onChange={handleInputChange}
              required
              isInvalid={!!errors.section}
              style={{
                fontSize: '0.875rem',
                padding: '0.5rem',
                border: '1px solid #D1D5DB',
                borderRadius: '0.375rem',
                transition: 'border-color 0.3s ease',
                width: '100%',
                maxWidth: '300px',
              }}
              className="input-field"
            >
              <option value="">Select a section</option>
              <option value="math">Math</option>
              <option value="science">Science</option>
              <option value="history">History</option>
              <option value="english">English</option>
              <option value="geography">Geography</option>
              <option value="other">Other</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">{errors.section}</Form.Control.Feedback>
          </div>

          {formData.section === 'other' && (
            <div>
              <label style={styles.label}>Custom Section</label>
              <Form.Control
                type="text"
                value={customSection}
                onChange={handleCustomSectionChange}
                required
                isInvalid={!!errors.customSection}
                placeholder="Enter custom section"
                style={{
                  fontSize: '0.875rem',
                  padding: '0.5rem',
                  border: '1px solid #D1D5DB',
                  borderRadius: '0.375rem',
                  transition: 'border-color 0.3s ease',
                  width: '100%',
                  maxWidth: '300px',
                }}
                className="input-field"
              />
              <Form.Control.Feedback type="invalid">{errors.customSection}</Form.Control.Feedback>
            </div>
          )}

          <div style={{ width: '100%' }}>
            <label style={styles.label}>CSV File</label>
            <div style={styles.fileInputContainer}>
              <input
                id="csvFile"
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                required
                style={styles.fileInput}
                className="input-field"
              />
              <label htmlFor="csvFile" style={styles.button} className="glow-button">
                Choose File
              </label>
              <div style={styles.fileName} title={fileName}>
                {fileName}
              </div>
            </div>
            <Form.Control.Feedback type="invalid" style={{ display: errors.file ? 'block' : 'none' }}>
              {errors.file}
            </Form.Control.Feedback>
            <div style={styles.note}>
              Note: CSV should have headers: questionText, options, correctAnswer, difficulty.<br />
              Options should be dot-separated (e.g., "Option1.Option2.Option3").<br />
              Difficulty must be one of: easy, medium, hard.
            </div>
          </div>

          <div style={styles.uploadButtonContainer}>
            <button
              type="submit"
              disabled={isUploading}
              style={{
                ...styles.button,
                ...(isUploading ? styles.buttonDisabled : {}),
              }}
              className="glow-button"
            >
              {isUploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </form>
      </div>

      {showQuestionModal && (
        <QuestionModal
          show={showQuestionModal}
          onHide={() => setShowQuestionModal(false)}
          onSave={handleSaveQuestion}
        />
      )}
    </div>
  );
};

export default BulkUploadQuestions;